import { Effect } from 'effect'

type StoreName = string

type AnyEvent = { type?: string } | string

type TimelineLike = { messages?: () => readonly AnyEvent[] }
type TimelineStore = { getTimeline?: () => TimelineLike | undefined }
type QaDevTools = {
  marks?: Record<string, number>
  mark?: (storeName?: StoreName) => { storeName: StoreName; before: number }
  delta?: (storeName?: StoreName) => {
    storeName: StoreName
    before: number
    after: number
    deltaLen: number
    counts: Record<string, number>
    types: string[]
  }
  last?: (n?: number, storeName?: StoreName) => string[]
  repeated?: (storeName?: StoreName) => [string, number][]
  reloadMark?: (value?: string) => string
  reloadCheck?: () => string | null
}

type DevStores = Record<string, QaDevTools | unknown>
type WindowWithReduxDevtools = typeof globalThis & {
  __stores__?: DevStores
  __qaReloadMarker?: string
}

function getWindow(): WindowWithReduxDevtools {
  return globalThis as WindowWithReduxDevtools
}

function getHistory(storeName: StoreName): AnyEvent[] {
  const w = getWindow()
  const maybeStore = w.__stores__?.[storeName] as TimelineStore | undefined
  const timelineMessages = maybeStore?.getTimeline?.()?.messages?.()
  return Array.isArray(timelineMessages) ? [...timelineMessages] : []
}

function typeOfEvent(e: AnyEvent): string {
  return typeof e === 'string' ? e : e.type ?? '<unknown>'
}

function countsOfTypes(types: string[]): Record<string, number> {
  return types.reduce(
    (m, t) => {
      m[t] = (m[t] ?? 0) + 1
      return m
    },
    {} as Record<string, number>
  )
}

export function installQaHelpers(defaultStoreName: StoreName) {
  if (!import.meta.env.DEV) return Effect.void

  return Effect.acquireRelease(
    Effect.sync(() => {
      const window = getWindow()
      const stores = (window.__stores__ ??= {})
      const devKey = `${defaultStoreName}_dev`
      const dev: QaDevTools = {}
      const marks: Record<string, number> = {}
      dev.marks = marks
      stores[devKey] = dev

      dev.mark = (storeName: StoreName = defaultStoreName) => {
        const history = getHistory(storeName)
        marks[storeName] = history.length
        return { storeName, before: history.length }
      }

      dev.delta = (storeName: StoreName = defaultStoreName) => {
        const history = getHistory(storeName)
        const before = marks[storeName] ?? 0
        const delta = history.slice(before)
        const types = delta.map(typeOfEvent)
        return {
          storeName,
          before,
          after: history.length,
          deltaLen: delta.length,
          counts: countsOfTypes(types),
          types
        }
      }

      dev.last = (n: number = 10, storeName: StoreName = defaultStoreName) => {
        const history = getHistory(storeName)
        return history.slice(-n).map(typeOfEvent)
      }

      dev.repeated = (storeName: StoreName = defaultStoreName) => {
        const history = getHistory(storeName)
        const types = history.map(typeOfEvent)
        const counts = countsOfTypes(types)
        return Object.entries(counts)
          .filter(([, count]) => count > 1)
          .sort((left, right) => right[1] - left[1])
      }

      dev.reloadMark = (value: string = 'keep') => {
        window.__qaReloadMarker = value
        return window.__qaReloadMarker
      }

      dev.reloadCheck = () => window.__qaReloadMarker ?? null

      return { dev, devKey, stores }
    }),
    ({ dev, devKey, stores }) =>
      Effect.sync(() => {
        if (stores[devKey] == dev) {
          delete stores[devKey]
        }
      })
  ).pipe(Effect.asVoid)
}
